import React from 'react';
import { RadioButton } from '@brighterion/br-ui';
import { useTranslation } from 'react-i18next';
import { addMinutes, parseDateInTimeZone, timeZone } from '@brighterion/utils/date-timezone';
import { LIST_ENTRY_DATE_FORMAT, LIST_EXPIRY_START_TIME_MINUTES_OFFSET } from '@brighterion/utils/constants';
import { Field, FieldProps, FormikProps } from 'formik';
import { GeneralListEntry, ListEntryApiResponseObj, PanListEntry, RangeListEntry } from '@brighterion/types/listEntry';
import DatePickerWrapper from '../DatePickerWrapper/DatePickerWrapper';
import styles from './StartDateTimePicker.css';

type Props = {
    formikProps: FormikProps<GeneralListEntry | PanListEntry | RangeListEntry | ListEntryApiResponseObj>;
};

const StartDateTimePicker = ({ formikProps }: Props) => {
    const { t } = useTranslation();
    const { setFieldValue, isSubmitting } = formikProps;
    const [startNow, setStartNow] = React.useState(true);

    React.useEffect(() => {
        if (formikProps?.values?.startTime) {
            setStartNow(false);
        } else {
            setStartNow(true);
        }
    }, []);

    return (
        <div
            data-testid='select-startTime-content'
            role='radiogroup'
            aria-labelledby='select-startTime-option'
            className={styles.radioGroup}
        >
            <div id='select-startTime-option' className={styles.radioButtonGroupLabel}>
                {t('Start Date and Time')}
            </div>
            <RadioButton
                dataTestId='startTime-now'
                id='startTime-now'
                text='Now'
                name='startTime-now-option'
                value={startNow ? 'true' : 'false'}
                onChange={() => {
                    setStartNow(true);
                    // useAddNewListEntry will set startTime to now if startTime is empty
                    // this is a design decision to meet UX requirements
                    setFieldValue('startTime', '');
                }}
                aria-labelledby='startTime-now'
                className={styles.radioButton}
                checked={startNow}
                disabled={isSubmitting}
            />

            <RadioButton
                dataTestId='startTime-custom'
                id='startTime-custom'
                text='Custom'
                name='startTime-custom-option'
                value='false'
                onChange={() => {
                    setStartNow(false);
                    // set default date time
                    setFieldValue(
                        'startTime',
                        parseDateInTimeZone(
                            addMinutes(new Date(), LIST_EXPIRY_START_TIME_MINUTES_OFFSET),
                            timeZone,
                            LIST_ENTRY_DATE_FORMAT
                        )
                    );
                }}
                aria-labelledby='startTime-custom'
                className={styles.radioButton}
                checked={!startNow}
                disabled={isSubmitting}
            />

            {!startNow && (
                <Field name='startTime'>
                    {({ field, form: { errors } }: FieldProps) => {
                        return (
                            <div className={styles.startTimeContainer}>
                                <DatePickerWrapper
                                    selected={new Date(field.value)}
                                    onChange={(date: any) => setFieldValue('startTime', date)}
                                    disabled={isSubmitting}
                                    showIcon
                                />
                                {errors[field.name] && <div className={styles.labelError}>{errors[field.name]}</div>}
                            </div>
                        );
                    }}
                </Field>
            )}
        </div>
    );
};

export default StartDateTimePicker;
